import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { validateEmail, validateFullName } from '../../../formValidators';
import CompanyRegistrationDropdown from '../../common/CompanyRegistrationDropdown';
import StyledTextField from '../../common/StyledTextField';
import PasswordInput from '../../PasswordInput';
import SocialLoginForm from '../SocialLoginForm';
import { analyticsTrack } from '../../../analyticsUtils';

const renderAgreementLinks = () => (
  <div className="terms-and-privacy form-group body-text-2">
    <div className="terms-and-privacy-label body-text-3 normal-weight">
      By signing up, you agree to
      <a href="/terms-of-use"> Clora&#39;s Terms of Use</a>
      ,
      <a href="/master_services_agreement"> Master Service Agreement</a>
      {' '}
      and
      <a href="/privacy-policy"> Privacy Policy</a>
      .
    </div>
  </div>
);

class EmployerSignupForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      email: props.invitedEmail || '',
      password: '',
      company: props.company || '',
      userName: props.userName || '',
      errors: {},
      submitted: false,
      showLocalRegistrationFields: false,
    };
    this.handleUpdateField = this.handleUpdateField.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggleLocalRegistrationFields = this.toggleLocalRegistrationFields.bind(this);
  }

  toggleLocalRegistrationFields() {
    const { showLocalRegistrationFields } = this.state;
    const { location } = this.props;
    analyticsTrack('Clicked continue during registration', { location });
    this.setState({ showLocalRegistrationFields: !showLocalRegistrationFields });
  }

  handleValidate() {
    const {
      email, userName, password, company, errors,
    } = this.state;

    const fullNameValidation = validateFullName(userName);
    const emailValidation = validateEmail(email);
    this.setState({
      errors: {
        ...errors,
        email: emailValidation.error,
        userName: fullNameValidation.error,
        password: password.length ? null : 'Please fill out this field.',
        company: company.length ? null : 'Please fill out this field.',
      },
    });
    return !emailValidation.error && fullNameValidation.isValid && password.length
      && company.length;
  }

  handleUpdateField(field) {
    return (e) => {
      if (field === 'company') {
        this.setState({ company: e.value });
      } else {
        this.setState({ [field]: e.target.value });
      }
    };
  }

  handleSubmit(e) {
    if (this.handleValidate()) {
      this.setState({ submitted: true });
    } else {
      e.preventDefault();
    }
  }

  render() {
    const {
      csrfToken,
      invitedEmail,
      referralToken,
      submitButtonText,
      signInUrl,
      location,
      googleOmniauthUrl,
      msOmniauthUrl,
      extraParams,
      inviterId,
      projectResourceId,
    } = this.props;
    const {
      userName, company, email, errors, submitted, showLocalRegistrationFields,
    } = this.state;

    return (
      <form
        id="create_employer"
        action="/registration/employers"
        acceptCharset="UTF-8"
        method="post"
        onSubmit={this.handleSubmit}
      >
        {Object.keys(extraParams).map((key) => (
          <input type="hidden" name={key} value={extraParams[key]} />
        ))}
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <input type="hidden" name="invited_email" value={invitedEmail} />
        <input type="hidden" name="referral_token" value={referralToken} />
        <input type="hidden" name="page_location" value={location} />
        <input type="hidden" name="project_resource_id" value={projectResourceId} />
        <input type="hidden" name="inviter_id" value={inviterId} />
        <StyledTextField
          id="email"
          className="form-control fs-hide validate work-email"
          name="user[email]"
          label="Work Email"
          value={email}
          error={!!errors.email}
          helperText={errors.email}
          margin="normal"
          onChange={this.handleUpdateField('email')}
        />
        { showLocalRegistrationFields
          ? (
            <Fragment>
              <StyledTextField
                id="full_name"
                className="form-control fs-hide mb-25"
                value={userName}
                error={!!errors.userName}
                helperText={errors.userName}
                onChange={this.handleUpdateField('userName')}
                name="user[full_name]"
                label="Full Name"
                type="text"
                margin="normal"
                autoFocus
              />
              <CompanyRegistrationDropdown
                error={errors.company}
                value={company}
                onChange={this.handleUpdateField('company')}
              />
              <PasswordInput
                errors={errors.password}
                title="Password"
                handleChange={this.handleUpdateField('password')}
              />
              <div className="actions">
                <button
                  type="submit"
                  name="commit"
                  id={`employer-sign-up-${location}`}
                  className="btn btn-primary bigger-button full-width"
                  disabled={submitted}
                  onClick={() => analyticsTrack('Clicked on sign up', { location })}
                >
                  {submitButtonText}
                </button>
              </div>
            </Fragment>
          )
          : (
            <div className="actions">
              <button
                onClick={this.toggleLocalRegistrationFields}
                className="btn btn-primary bigger-button full-width mb-15"
              >
                Continue
              </button>
              { (googleOmniauthUrl || msOmniauthUrl)
              && (
              <SocialLoginForm
                googleOmniauthUrl={googleOmniauthUrl}
                msOmniauthUrl={msOmniauthUrl}
                state="register_employer"
                location={location}
              />
              )}
            </div>
          )}
        { renderAgreementLinks() }
        <div className="body-text-2 medium-weight text-center log-in-links">
          Already have a Clora account?&nbsp;
          <a className={`employer-registration-to-log-in-${location}  p-0`} href={signInUrl}>Log in</a>
        </div>
      </form>
    );
  }
}

EmployerSignupForm.propTypes = {
  userName: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  invitedEmail: PropTypes.string,
  company: PropTypes.string,
  referralToken: PropTypes.string,
  submitButtonText: PropTypes.string,
  signInUrl: PropTypes.string.isRequired,
  location: PropTypes.string,
  googleOmniauthUrl: PropTypes.string,
  msOmniauthUrl: PropTypes.string,
  extraParams: PropTypes.shape({}),
  inviterId: PropTypes.string,
  projectResourceId: PropTypes.string,
};

EmployerSignupForm.defaultProps = {
  userName: '',
  invitedEmail: null,
  company: null,
  referralToken: null,
  submitButtonText: 'Sign Up as an Employer',
  location: 'registration',
  extraParams: {},
  googleOmniauthUrl: null,
  msOmniauthUrl: null,
  inviterId: null,
  projectResourceId: null,
};

export default EmployerSignupForm;
